import merge from 'lodash/merge';

import { colors } from 'ui/theme';
import { TextSegment } from 'ui/workflows/flowchart/types';
import { ButtonType } from '../../constants';

interface CreateLinkSegmentArgsBase {
  /** Is the link enabled? */
  isEnabled?: boolean;
  /** Any additional props to be passed to the segment */
  [key: string]: any;
}

interface CreateLinkSegmentArgs extends CreateLinkSegmentArgsBase {
  /**
   * The url that the link segment will navigate to.
   * If this is not defined, the link will be disabled
   */
  url?: string | null;
}

interface CreateLinkButtonSegmentArgs extends CreateLinkSegmentArgsBase {
  /**
   * The type of button in the segment.
   * Useful for quick identification of what should happen onClick
   */
  buttonType?: string;
}

const linkSegmentProps = { isUnderlined: true, isDashed: true };
export const linkSegmentEnabledStyle = { fill: colors.textLink, cursor: 'pointer' };
export const linkSegmentDisabledStyle = { fill: colors.linkDisabled, cursor: 'not-allowed' };

/** Gets the segment for an enabled link */
const getEnabledLinkSegment = (text: string): TextSegment => ({
  ...linkSegmentProps,
  text,
  style: linkSegmentEnabledStyle,
});

/** Gets the segment for a disabled link */
const getDisabledLinkSegment = (text: string): TextSegment => ({
  ...linkSegmentProps,
  text,
  style: linkSegmentDisabledStyle,
});

/** Creates a segment which would render looking and acting like a link */
export const createLinkSegment = (text: string, args: CreateLinkSegmentArgs = {}): TextSegment => {
  const { url, isEnabled = true, ...rest } = args;
  const segmentBase =
    url && isEnabled ? { ...getEnabledLinkSegment(text), url } : getDisabledLinkSegment(text);
  return merge({}, segmentBase, rest);
};

/** Creates a segment which looks like a link but acts like a button */
export const createLinkButtonSegment = (
  text: string,
  args: CreateLinkButtonSegmentArgs = {},
): TextSegment => {
  const { isEnabled = true, buttonType = ButtonType.ButtonLink, ...rest } = args;
  const segmentBase = isEnabled ? getEnabledLinkSegment(text) : getDisabledLinkSegment(text);

  return merge({ props: { buttonType } }, segmentBase, rest, {
    isButtonLink: true,
  });
};
